export interface ScoreColorConfig {
    score: number;
    hexColor: string;
    tailwindClass: string;
}

// Consistent color configuration across all components
const SCORE_COLORS: ScoreColorConfig[] = [
    { score: 0, hexColor: "#C8CED4", tailwindClass: "text-neutral-500" }, // Neutral gray for 0
    { score: 30, hexColor: "#F59E0B", tailwindClass: "text-alert-600" }, // Orange
    { score: 60, hexColor: "#F59E0B", tailwindClass: "text-alert-600" }, // Orange
    { score: 80, hexColor: "#40C057", tailwindClass: "text-success-600" }, // Green
    { score: 100, hexColor: "#40C057", tailwindClass: "text-success-600" }, // Green
];

// Helper function to interpolate between two hex colors
const interpolateColor = (
    color1: string,
    color2: string,
    ratio: number,
): string => {
    const r1 = parseInt(color1.slice(1, 3), 16);
    const g1 = parseInt(color1.slice(3, 5), 16);
    const b1 = parseInt(color1.slice(5, 7), 16);

    const r2 = parseInt(color2.slice(1, 3), 16);
    const g2 = parseInt(color2.slice(3, 5), 16);
    const b2 = parseInt(color2.slice(5, 7), 16);

    const r = Math.round(r1 + (r2 - r1) * ratio);
    const g = Math.round(g1 + (g2 - g1) * ratio);
    const b = Math.round(b1 + (b2 - b1) * ratio);

    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

/**
 * Get hex color for score with smooth interpolation (for ScoreGauge)
 */
export const getScoreHexColor = (score: number): string => {
    // Find the appropriate color range
    for (let i = 0; i < SCORE_COLORS.length - 1; i++) {
        const current = SCORE_COLORS[i];
        const next = SCORE_COLORS[i + 1];

        if (score >= current.score && score <= next.score) {
            // Interpolate between colors
            const ratio =
                (score - current.score) / (next.score - current.score);
            return interpolateColor(current.hexColor, next.hexColor, ratio);
        }
    }

    return SCORE_COLORS[SCORE_COLORS.length - 1].hexColor;
};

/**
 * Get Tailwind class for score (for AtsScanHistoryModal)
 */
export const getScoreTailwindClass = (score: number): string => {
    // Find the appropriate color range
    for (let i = SCORE_COLORS.length - 1; i >= 0; i--) {
        if (score >= SCORE_COLORS[i].score) {
            return SCORE_COLORS[i].tailwindClass;
        }
    }

    return SCORE_COLORS[0].tailwindClass;
};

/**
 * Get color configuration for a given score
 */
export const getScoreColorConfig = (score: number): ScoreColorConfig => {
    for (let i = SCORE_COLORS.length - 1; i >= 0; i--) {
        if (score >= SCORE_COLORS[i].score) {
            return SCORE_COLORS[i];
        }
    }

    return SCORE_COLORS[0];
};

/**
 * Get Tailwind class for score with type (text/bg) (for ScanItem)
 */
export const getScoreTailwindClassWithType = (
    score: number,
    type: "text" | "bg",
): string => {
    const baseClass = getScoreTailwindClass(score);

    if (type === "bg") {
        // Convert text classes to background classes
        if (baseClass === "text-success-600") return "bg-success-600";
        if (baseClass === "text-alert-600") return "bg-alert-600";
        if (baseClass === "text-neutral-500") return "bg-neutral-500";
    }

    return baseClass;
};

/**
 * Get badge color classes for section scores (for ScanSection)
 */
export const getScoreBadgeClasses = (score: number): string => {
    if (score >= 80) return "bg-success-100 text-success-800";
    if (score >= 60) return "bg-alert-100 text-alert-800";
    return "bg-error-100 text-error-500";
};
